import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle, StatCard } from "@/components/GlassBits";
import { formatINR, formatDate, statusBadgeClass } from "@/lib/format";
import { FileDown, FileText, Calendar } from "lucide-react";

const PERIODS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "custom", label: "Custom range" },
];

export default function Statement() {
  const [period, setPeriod] = useState("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const params = { period };
      if (period === "custom" && start && end) {
        params.start = new Date(start).toISOString();
        params.end = new Date(end + "T23:59:59").toISOString();
      }
      const { data } = await api.get("/customer/statement", { params });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const download = async (fmt) => {
    const params = new URLSearchParams({ period });
    if (period === "custom" && start && end) {
      params.set("start", new Date(start).toISOString());
      params.set("end", new Date(end + "T23:59:59").toISOString());
    }
    const token = localStorage.getItem("ldc_token");
    const url = `${API}/customer/statement.${fmt}?${params.toString()}`;
    try {
      const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const a = document.createElement("a");
      const dl = URL.createObjectURL(blob);
      a.href = dl;
      a.download = `statement-${period}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Account" title="Statement" right={
        <div className="flex gap-2">
          <button onClick={() => download("pdf")} className="ldc-btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="statement-pdf-btn"><FileDown size={14}/> PDF</button>
          <button onClick={() => download("csv")} className="px-4 py-2 rounded-md text-sm border border-white/10 hover:border-[#fbce18]/40 inline-flex items-center gap-2" data-testid="statement-csv-btn"><FileText size={14}/> CSV</button>
        </div>
      } />

      <div className="ldc-glass p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button key={p.key} onClick={() => setPeriod(p.key)} className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2 ${period === p.key ? "bg-[#fbce18] text-black" : "border border-white/10 text-white/60 hover:text-white"}`} data-testid={`statement-period-${p.key}`}>
              <Calendar size={12}/> {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2 ml-auto">
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="statement-start" />
            <span className="text-white/40">→</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="ldc-input rounded-md px-3 py-2 text-sm" data-testid="statement-end" />
            <button onClick={load} className="px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] bg-[#fbce18] text-black">Apply</button>
          </div>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <StatCard testid="statement-stat-credit" label="Total Credit" value={formatINR(data.total_credit)} accent />
          <StatCard testid="statement-stat-debit" label="Refunds" value={formatINR(data.total_debit)} />
          <StatCard testid="statement-stat-net" label="Net" value={formatINR(data.net)} />
          <StatCard testid="statement-stat-count" label="Entries" value={data.rows.length} />
        </div>
      )}

      <div className="ldc-glass overflow-hidden">
        <div className="overflow-x-auto ldc-scroll">
          <table className="w-full ldc-table" data-testid="statement-table">
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Transaction / Invoice</th>
                <th>Description</th>
                <th>Method</th>
                <th>Type</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Balance After</th>
              </tr>
            </thead>
            <tbody>
              {!data && (<tr><td colSpan={7} className="text-center py-10 text-white/40">Loading…</td></tr>)}
              {data?.rows.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-white/40">No entries in the selected period.</td></tr>)}
              {data?.rows.map((r, i) => (
                <tr key={r.tx_id + i}>
                  <td className="text-xs text-white/60">{formatDate(r.date)}</td>
                  <td>
                    <div className="font-mono text-xs">{r.tx_id}</div>
                    <div className="font-mono text-[10px] text-[#fbce18]">{r.invoice_no}</div>
                  </td>
                  <td className="text-sm">{r.description}</td>
                  <td className="uppercase text-xs">{r.method}</td>
                  <td><span className={statusBadgeClass(r.status)}>{r.status}</span></td>
                  <td className={`text-right font-medium ${r.direction === "debit" ? "text-red-400" : r.direction === "credit" ? "text-emerald-400" : "text-white/70"}`}>
                    {r.direction === "debit" ? "-" : r.direction === "credit" ? "+" : ""} {formatINR(r.amount)}
                  </td>
                  <td className="text-right font-mono">{formatINR(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
